<script setup lang="ts">
// Libraries import
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useWorkerMissionActions } from "@/composables/missions/worker-actions/useWorkerActions";
import { ModalName } from '@/constants/modals';
import { ROUTES } from '@/constants/routes';
import { MissionCommand, WorkerMissionStatus } from '@/types/mission';

import type { IWorkerSelection } from "@/modules/missions/types";

// Components import
import { type AvatarProps, AvatarSize } from "@/dsComponents/Avatar";
import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import ProgressCircle from "@/dsComponents/progress/ProgressCircle.vue";
import Separator from "@/dsComponents/Separator.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import WorkerActions from "@/components/Mission/WorkerActions.vue";

// Data and type import
import { blueBase, green700, neutral900, redBase, yellow700 } from "@/color";

interface WorkersStatusSectionProps {
    status: WorkerMissionStatus;
    positionQuantity: number;
    workers: IWorkerSelection[];
    missionsAvailableCommands: MissionCommand[];
    isOpen?: boolean;
}

const props = defineProps<WorkersStatusSectionProps>();
const { t } = useI18n();
const router = useRouter();
const emits = defineEmits<{
    (event: "onWorkerAction", workerId: string[], action: MissionCommand): void;
    (event: "openMiniRead", worker: string): void;
    (event: "toggleShow", status: WorkerMissionStatus): void;
}>();

const modalStore = useModalStore();

const { workerMissionActionsProvider } = useWorkerMissionActions();

const isConfirmedStatusSection = computed(() => {
    return props.status === WorkerMissionStatus.CONFIRMED;
});

const workerIdSelected = ref<string>();

const confirmedWorkerRatio = computed(() => {
    const confirmedWorkers = props.workers.filter((worker) => worker.status === WorkerMissionStatus.CONFIRMED).length;
    if (!confirmedWorkers) return 0;
    return (confirmedWorkers / props.positionQuantity) * 100;
});

const tableAltcolums = [
    {
        field: "name",
        header: "Name",
    },
    {
        field: "phone",
        header: "Phone",
        width: "100px",
        displayOnHover: true,
    },
];
const globalActions = computed(() => {
    if (props.status === WorkerMissionStatus.NOT_CONFIRMED) return undefined;

    const availableActions = [...new Set(props.workers.flatMap((e) => e.availableCommands))];

    return {
        width: "28px",
        rowActions: [
            {
                component: WorkerActions,
                props: {
                    availableActions: availableActions,
                },
                action: (index: number) => {
                    workerIdSelected.value = props.workers[index]?.workerId;
                },
            },
        ],
    };
});
const formatedData = computed(() => {
    return props.workers.map((worker, rowIndex) => {
        const content = {
            name: {
                customElement: {
                    component: Avatar,
                    props: {
                        size: AvatarSize.size16 as unknown as AvatarSize,
                        firstname: worker.firstname,
                        lastname: worker.lastname,
                        displayName: true,
                        fontSize: "12px",
                    } as AvatarProps,
                },
            },
            phone: {
                text: {
                    value: worker.phone ?? "",
                    passive: true,
                    customClass: "text-vertical-align-bottom",
                },
            },
        };

        return {
            content,
            editMode: false,
            rowIndex,
        };
    });
});

const getStatusColor = (status: WorkerMissionStatus) => {
    switch (status) {
        case WorkerMissionStatus.CONFIRMED:
            return blueBase;
        case WorkerMissionStatus.ACCEPTED:
            return green700;
        case WorkerMissionStatus.PROPOSED:
        case WorkerMissionStatus.REPROPOSED:
        case WorkerMissionStatus.REPROPOSED_BY_SMS:
            return yellow700;
        case WorkerMissionStatus.REJECTED_BY_AGENCY:
        case WorkerMissionStatus.ABORTED:
            return redBase;
        case WorkerMissionStatus.DECLINED:
        case WorkerMissionStatus.NOT_CONFIRMED:
            return neutral900;
        default:
            return neutral900;
    }
};

const statusI18nKey: Record<WorkerMissionStatus, string> = {
    [WorkerMissionStatus.PROPOSED]: "proposed",
    [WorkerMissionStatus.REPROPOSED]: "reproposed",
    [WorkerMissionStatus.REPROPOSED_BY_SMS]: "reproposedBySms",
    [WorkerMissionStatus.ACCEPTED]: "accepted",
    [WorkerMissionStatus.CONFIRMED]: "confirmed",
    [WorkerMissionStatus.DECLINED]: "declined",
    [WorkerMissionStatus.REJECTED_BY_AGENCY]: "rejectedByAgency",
    [WorkerMissionStatus.ABORTED]: "aborted",
    [WorkerMissionStatus.NOT_CONFIRMED]: "notconfirmed",
};

const getLabel = (status: WorkerMissionStatus) => {
    const workerCount = props.workers.length > 1 ? "plural" : "singular";
    const statusKey = statusI18nKey[status];
    switch (status) {
        case WorkerMissionStatus.ACCEPTED:
        case WorkerMissionStatus.PROPOSED:
        case WorkerMissionStatus.REJECTED_BY_AGENCY:
        case WorkerMissionStatus.ABORTED:
        case WorkerMissionStatus.DECLINED:
        case WorkerMissionStatus.NOT_CONFIRMED:
            return t(`mission.workerStatus.${statusKey}.agency`);
        case WorkerMissionStatus.CONFIRMED:
        case WorkerMissionStatus.REPROPOSED:
        case WorkerMissionStatus.REPROPOSED_BY_SMS:
            return t(`mission.workerStatus.${statusKey}.${workerCount}`);
        default:
            break;
    }
};

const toggleShowTableSection = () => {
    emits("toggleShow", props.status);
};

const statusWithActionOnAll = computed(() => {
    return props.status === WorkerMissionStatus.PROPOSED;
});

const actionOnAll = (status: WorkerMissionStatus): MissionCommand[] => {
    switch (status) {
        case WorkerMissionStatus.PROPOSED:
            return [MissionCommand.repropose];
        default:
            return [];
    }
};

const handleAction = (value: MissionCommand) => {
    if (value === MissionCommand.propose || value === MissionCommand.repropose) {
        emits("onWorkerAction", [""], value);
        return;
    }

    if (!workerIdSelected.value) return;
    if (value === MissionCommand.workerDetails) {
        modalStore.actions.close(ModalName.MissionSelection);
        router.push({
            name: ROUTES.APP.WORKERS.PROFIL._ROOT,
            params: {
                interimId: workerIdSelected.value,
            },
        });
        return;
    }
    emits("onWorkerAction", [workerIdSelected.value], MissionCommand[value]);
};

const openMiniRead = (index: number) => {
    const workerId = props.workers[index].workerId;
    emits("openMiniRead", workerId);
};

onBeforeMount(() => {
    workerMissionActionsProvider(handleAction);
});
</script>
<template>
    <div>
        <div class="worker-selection-by-status">
            <div class="section-title">
                <div class="section-title-and-count">
                    <Button
                        v-if="isConfirmedStatusSection"
                        size="sm"
                        type="neutral"
                        variant="ghost"
                        :leftIcon="{ icon: 'status-marker', color: getStatusColor(status) }"
                        :label="getLabel(status)"
                    ></Button>
                    <Button
                        v-else
                        size="sm"
                        variant="ghost"
                        type="neutral"
                        :leftIcon="{ icon: 'status-marker', color: getStatusColor(status) }"
                        :rightIcon="isOpen ? 'caret-down-filled' : 'caret-right-filled'"
                        @click="toggleShowTableSection"
                        :label="getLabel(status)"
                    ></Button>
                    <ProgressCircle
                        v-if="isConfirmedStatusSection"
                        :fill="confirmedWorkerRatio"
                        :checked="confirmedWorkerRatio >= 100"
                        :labelRight="`${workers.length}/${positionQuantity}`"
                    />
                    <p v-else class="section-count">{{ workers.length }}</p>
                </div>
                <div v-if="statusWithActionOnAll" class="section-title-action">
                    <WorkerActions :availableActions="actionOnAll(status)" />
                </div>
            </div>
            <div v-if="isOpen && formatedData.length">
                <TableAlt
                    rowHover
                    :columns="tableAltcolums"
                    :rows="formatedData"
                    :globalActions="globalActions"
                    @action="handleAction"
                    rowClick
                    @rowClick="openMiniRead"
                />
            </div>
        </div>
        <Separator />
    </div>
</template>
<style scoped lang="scss">
.worker-selection-by-status {
    padding: 24px 12px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .section-title {
        display: flex;
        height: 28px;
        justify-content: space-between;

        .section-title-and-count {
            display: flex;
            gap: 8px;
            align-items: center;
        }
        .section-title-action {
            padding: 0 4px;
            text-align: center;
        }
    }
}

.section-count {
    color: $text-sub-600;
    @include font-size-12;
}
</style>
